import { KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { styled } from "@mui/material/styles";
import BOMNavigation from "./BOMNavigation";
import ItemDetails from "./ItemDetails";
import {
  useSpecificationState,
  useSpecificationStateStore,
  isItem,
} from "./specificationState";
import { getItemAtPath } from "../../utils/conditions";
import {
  useValidationStateStore,
  buildFormulaDependencyMap,
  buildAttributeUuidTranslator,
} from "./validationState";
import Duplicate from "../../../icons/Duplicate";
import Pencil from "../../../icons/Pencil";

import { useGetConditionsFromExp } from "../../../hooks/queries/useSpecQueries";
import { useGetWorkOrdersFromExp } from "../../../hooks/queries/useSpecQueries";
import { getGroupIdsForCondition } from "./statusState";
import Button from "../../../components/Button";
import ModifyConditionsModal from "./ModifyConditionsModal";
import {
  Box,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  Chip,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";

const Container = styled(Box)(({ theme }) => ({
  display: "flex",
  width: "100%",
  maxHeight: "calc(100vh - 200px)",
  padding: theme.spacing(3),
  gap: theme.spacing(3),
  backgroundColor: "#ffffff",
  boxSizing: "border-box",
}));

interface BOMViewProps {
  onOpenAddCondition: () => void;
  readOnly?: boolean;
  // Duplicating creates a new, unsubmitted condition, so it is gated
  // separately from `readOnly` (which also locks submitted conditions).
  duplicateDisabled?: boolean;
  constraints: ConstraintsResponse | null;
  constraintsFetched: boolean;
}

const Header = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: theme.spacing(3),
  paddingBottom: 0,
  backgroundColor: "#ffffff",
  gap: theme.spacing(2),
}));

const BOMView = ({
  onOpenAddCondition,
  readOnly = false,
  duplicateDisabled = false,
  constraints,
  constraintsFetched,
}: BOMViewProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [duplicateSeed, setDuplicateSeed] = useState<DuplicateConditionSeed>();
  const [isEditingConditionName, setIsEditingConditionName] = useState(false);
  const conditionNameInputRef = useRef<HTMLInputElement | null>(null);

  const condition = useSpecificationStateStore((state) => state.condition);
  const activeModifyConditionId = useSpecificationStateStore(
    (state) => state.activeModifyConditionId
  );
  const activeBomItemPath = searchParams.get("bom-item") || "";
  const activeItem = useMemo(() => {
    if (!condition) {
      return null;
    }
    return getItemAtPath(condition, activeBomItemPath) || condition;
  }, [condition, activeBomItemPath]);

  const {
    checkUnsavedChangesBeforeNavigation,
    hasUnsavedChanges,
    updateConditionName,
  } = useSpecificationState(activeItem);

  const setCondition = useSpecificationStateStore(
    (state) => state.setCondition
  );
  const setFormulaDependencies = useValidationStateStore(
    (state) => state.setFormulaDependencies
  );
  const setAttributeUuidTranslator = useValidationStateStore(
    (state) => state.setAttributeUuidTranslator
  );

  const handleItemClick = (item: ItemWithBOM) => {
    // Build the path to this item using absolute BOM indices
    if (!condition) return;

    const buildItemPath = (
      root: ItemWithBOM,
      targetItem: ItemWithBOM,
      path: number[] = []
    ): number[] | null => {
      // Check if this is the target item
      if (root === targetItem) {
        return path;
      }

      // Search through BOM children
      if (root.bom && root.bom.length > 0) {
        for (let i = 0; i < root.bom.length; i++) {
          const bomChild = root.bom[i];
          const newPath = [...path, i];
          const found = buildItemPath(bomChild, targetItem, newPath);
          if (found) return found;
        }
      }

      return null;
    };

    const itemPath = buildItemPath(condition, item);
    if (itemPath) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("bom-item", itemPath.join(">"));
      setSearchParams(newSearchParams, { replace: true });
    }
  };

  const { data: conditions } = useGetConditionsFromExp({ enabled: true });
  const { data: workOrders } = useGetWorkOrdersFromExp();

  // Use condition from state
  const activeCondition = condition;

  const handleConditionChange = (conditionId: number) => {
    if (!checkUnsavedChangesBeforeNavigation()) {
      return;
    }

    const selectedCondition = conditions!.find(
      (condition) => condition.cell_condition_id === conditionId
    );
    if (selectedCondition) {
      setCondition(selectedCondition);

      // Update URL with the condition ID
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set(
        "condition",
        String(selectedCondition.cell_condition_id)
      );
      setSearchParams(newSearchParams, { replace: true });
    }
  };

  useEffect(() => {
    if (!isEditingConditionName) {
      return;
    }

    conditionNameInputRef.current?.focus();
    conditionNameInputRef.current?.select();
  }, [isEditingConditionName]);

  useEffect(() => {
    setIsEditingConditionName(false);
  }, [activeCondition?.cell_condition_id]);

  const handleConditionNameCancel = () => {
    const baselineName =
      useSpecificationStateStore.getState().baselineConditionName ?? "";
    updateConditionName(baselineName);
    setIsEditingConditionName(false);
  };

  const commitConditionNameEdit = () => {
    const nextName = String(activeCondition?.name || "").trim();
    if (!nextName) {
      useSpecificationStateStore
        .getState()
        .setErrorMessage("Condition name cannot be blank.");
      handleConditionNameCancel();
      return;
    }
    updateConditionName(nextName);
    setIsEditingConditionName(false);
  };

  const handleConditionNameKeyDown = (
    event: KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      commitConditionNameEdit();
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      handleConditionNameCancel();
    }
  };

  useEffect(() => {
    if (conditions && conditions.length > 0) {
      const conditionParam = searchParams.get("condition");
      const currentConditionId = condition?.cell_condition_id;

      // Check if there's a condition ID in the URL
      if (conditionParam) {
        const selectedCondition = conditions.find(
          (c) => String(c.cell_condition_id) === conditionParam
        );

        if (!selectedCondition) {
          // Keep the pending condition id in the URL until data refresh contains it
          if (!condition) {
            setCondition(conditions[0]);
          }
          return;
        }

        // If we found a matching condition and it's different from the current one
        if (
          !condition ||
          currentConditionId !== selectedCondition.cell_condition_id
        ) {
          if (!checkUnsavedChangesBeforeNavigation()) {
            if (currentConditionId) {
              const newSearchParams = new URLSearchParams(searchParams);
              newSearchParams.set("condition", String(currentConditionId));
              setSearchParams(newSearchParams, { replace: true });
            }
            return;
          }

          setCondition(selectedCondition);
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.set(
            "condition",
            String(selectedCondition.cell_condition_id)
          );
          setSearchParams(newSearchParams, { replace: true });
          return;
        }

        if (condition) {
          // An unsaved root replacement always differs from the saved item.
          const shouldRefreshCondition =
            !condition.item_replaced &&
            (condition.external_spec_id !==
              selectedCondition.external_spec_id ||
              (isItem(condition.item) && isItem(selectedCondition.item)
                ? condition.item.uuid !== selectedCondition.item.uuid
                : condition.item.arena_part_number !==
                  selectedCondition.item.arena_part_number));
          if (shouldRefreshCondition) {
            setCondition(selectedCondition);
          }
        }
      } else {
        // If no condition param exists, set first condition and update URL
        const firstCondition = conditions[0];
        setCondition(firstCondition);

        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set(
          "condition",
          String(firstCondition.cell_condition_id)
        );
        setSearchParams(newSearchParams, { replace: true });
        return;
      }
    }
  }, [
    conditions,
    condition,
    checkUnsavedChangesBeforeNavigation,
    setCondition,
    setSearchParams,
    searchParams,
  ]);

  useEffect(() => {
    if (!condition) {
      return;
    }

    if (!activeBomItemPath) {
      return;
    }

    const foundItem = getItemAtPath(condition, activeBomItemPath);
    if (foundItem) {
      return;
    }

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete("bom-item");
    setSearchParams(newSearchParams, { replace: true });
  }, [condition, activeBomItemPath, searchParams, setSearchParams]);

  // Fetch constraints for the active condition when conditions are loaded
  // Conditions are always Items (never ArenaOnlyItems)
  const itemUuid =
    activeCondition && isItem(activeCondition.item)
      ? activeCondition.item.uuid
      : undefined;

  // Update constraints in the store when they are fetched for a new item
  useEffect(() => {
    if (!constraintsFetched || !itemUuid || !activeCondition) {
      return;
    }

    // Rebuild translator and dependencies whenever condition or constraints change
    const translator = buildAttributeUuidTranslator(activeCondition);
    setAttributeUuidTranslator(translator);

    const formulaDeps = buildFormulaDependencyMap(
      constraints || undefined,
      translator
    );
    setFormulaDependencies(formulaDeps);
  }, [
    constraints,
    constraintsFetched,
    itemUuid,
    activeCondition,
    setFormulaDependencies,
    setAttributeUuidTranslator,
  ]);

  const handleDuplicateConditionSubmit = ({
    createdConditionIds,
  }: DuplicateConditionSubmitResult) => {
    if (!createdConditionIds.length) {
      return;
    }

    const newestId = createdConditionIds[createdConditionIds.length - 1];
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("condition", String(newestId));
    setSearchParams(newSearchParams, { replace: true });
  };

  // TODO: this is bad but I'm not sure if value types are consistent between main and lmer-400
  // Helper function to safely extract attribute values
  const getAttributeValue = (attribute: any): string => {
    if (!attribute) return "";

    if (typeof attribute === "object" && "value" in attribute) {
      return String(attribute.value);
    }

    return String(attribute);
  };

  const handleDuplicateConditionClick = () => {
    if (!activeCondition) {
      return;
    }

    if (!isItem(activeCondition.item)) {
      return;
    }

    const item = activeCondition.item;
    const scale = getAttributeValue(item.attributes["Scale" as string]);
    const baseItemUUID = item.uuid;

    if (!baseItemUUID) {
      console.error("Missing base item UUID");
      return;
    }

    const seed: DuplicateConditionSeed = {
      basePayload: {
        scale: scale,
        base_item_uuid: baseItemUUID,
      },
      sourceName: activeCondition.name,
    };

    setDuplicateSeed(seed);
    setIsDuplicateModalOpen(true);
  };

  const isDuplicateDisabled =
    !activeCondition || hasUnsavedChanges || duplicateDisabled;
  const duplicateTooltipTitle = hasUnsavedChanges
    ? "Cannot duplicate until all changes are saved"
    : "";

  const getConditionStatusLabel = (conditionItem: FormspecCondition) => {
    if (activeModifyConditionId !== null) {
      // While a condition is being modified, every other condition is locked.
      return conditionItem.cell_condition_id === activeModifyConditionId
        ? "Modifying"
        : "Locked";
    }

    return getGroupIdsForCondition(workOrders, conditionItem.cell_condition_id)
      .length > 0
      ? "Submitted"
      : "Not Submitted";
  };

  const renderConditionLabel = (conditionItem: FormspecCondition) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        width: "100%",
      }}
    >
      <Chip
        label={getConditionStatusLabel(conditionItem)}
        size="small"
        sx={{
          backgroundColor: "#CFD8DC",
          color: "#455A64",
          fontWeight: 500,
          fontSize: "0.75rem",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: "100px",
        }}
      />

      {conditionItem.replicates > 0 && (
        <Chip
          label={`${conditionItem.replicates} replicate${
            conditionItem.replicates !== 1 ? "s" : ""
          }`}
          size="small"
          sx={{
            backgroundColor: "#607D8B",
            color: "white",
            fontWeight: 500,
            fontSize: "0.75rem",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "100px",
            marginRight: "5px",
          }}
        />
      )}
      {conditionItem.name}
    </Box>
  );

  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        backgroundColor: "#ffffff",
      }}
    >
      <Header>
        <Box sx={{ display: "flex", alignItems: "center", flex: 1, gap: 1 }}>
          {isEditingConditionName ? (
            <TextField
              inputRef={conditionNameInputRef}
              size="small"
              label="Condition"
              InputLabelProps={{ shrink: true }}
              value={activeCondition?.name || ""}
              onChange={(event) => updateConditionName(event.target.value)}
              onBlur={() => setIsEditingConditionName(false)}
              onKeyDown={handleConditionNameKeyDown}
              disabled={!activeCondition || readOnly}
              sx={{
                flex: 1,
                minWidth: 200,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#f8f8f8",
                },
                "& .MuiInputLabel-root": {
                  backgroundColor: "white",
                  transform: "translate(14px, -9px) scale(0.75)",
                  "&.Mui-focused, &.MuiFormLabel-filled": {
                    transform: "translate(14px, -9px) scale(0.75)",
                  },
                },
              }}
            />
          ) : (
            <FormControl
              size="small"
              sx={{
                flex: 1,
                minWidth: 200,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#f8f8f8",
                  "&.Mui-focused": {
                    backgroundColor: "#f8f8f8",
                  },
                },
                "& .MuiInputLabel-root": {
                  transform: "translate(14px, -9px) scale(0.75)",
                  "&.Mui-focused, &.MuiFormLabel-filled": {
                    transform: "translate(14px, -9px) scale(0.75)",
                  },
                },
              }}
            >
              <InputLabel
                id="condition-selector"
                style={{ backgroundColor: "white" }}
                shrink
              >
                Condition
              </InputLabel>

              <Select
                value={activeCondition?.cell_condition_id || ""}
                labelId="condition-selector"
                label="Condition"
                onChange={(e) => {
                  if (e.target.value === "add_new") {
                    if (activeModifyConditionId !== null) {
                      return;
                    }
                    onOpenAddCondition();
                  } else {
                    handleConditionChange(Number(e.target.value));
                  }
                }}
                renderValue={() =>
                  activeCondition ? renderConditionLabel(activeCondition) : null
                }
                displayEmpty
              >
                <MenuItem
                  value="add_new"
                  sx={{ fontStyle: "italic", color: "#1976d2" }}
                  disabled={activeModifyConditionId !== null}
                >
                  + Add New Condition
                </MenuItem>
                {conditions?.map((c) => (
                  <MenuItem
                    key={c.cell_condition_id}
                    value={c.cell_condition_id}
                  >
                    {renderConditionLabel(c)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Tooltip title="Edit condition name" placement="top">
            <span>
              <IconButton
                size="small"
                aria-label="Edit condition name"
                onClick={() => setIsEditingConditionName(true)}
                disabled={
                  !activeCondition || readOnly || isEditingConditionName
                }
              >
                <Pencil fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <Box sx={{ display: "flex" }}>
          <Tooltip
            title={duplicateTooltipTitle}
            placement="top"
            disableHoverListener={!hasUnsavedChanges}
          >
            <span>
              <Button
                color="tertiary"
                onClick={handleDuplicateConditionClick}
                disabled={isDuplicateDisabled}
                style={{
                  width: "fit-content",
                  borderColor: "#1976D2",
                  color: "#1976D2",
                  display: "flex",
                  gap: "8px",
                  alignItems: "center",
                  paddingLeft: "8px",
                  fontSize: "14px",
                }}
              >
                <Duplicate />
                DUPLICATE CONDITION
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Header>

      <Container>
        <BOMNavigation
          condition={activeCondition}
          activeItem={activeItem}
          onItemClick={handleItemClick}
        />
        <ItemDetails
          activeItem={activeItem}
          onItemClick={handleItemClick}
          conditionUuid={activeCondition?.external_spec_id || ""}
          readOnly={readOnly}
        />
      </Container>
      <ModifyConditionsModal
        mode="duplicate"
        open={isDuplicateModalOpen}
        onClose={() => {
          setIsDuplicateModalOpen(false);
          setDuplicateSeed(undefined);
        }}
        seedData={duplicateSeed}
        onSubmit={handleDuplicateConditionSubmit}
      />
    </Box>
  );
};

export default BOMView;
